<template>
    <div class="p-4">
        <!-- Stats Bar -->
        <div class="flex items-center space-x-4 mb-4 text-sm">
            <span class="text-slate-600">
                {{ totalRows }} total rows
            </span>
            <span class="text-green-600">
                {{ validCount }} valid
            </span>
            <span v-if="warningRowCount > 0" class="text-amber-600">
                {{ warningRowCount }} with warnings on this page
            </span>
            <span v-if="errorRowCount > 0" class="text-red-600">
                {{ errorRowCount }} with errors
            </span>
        </div>

        <p v-if="completedRowIds?.size" class="mb-3 text-sm text-slate-600">
            {{ completedRowIds.size }} rows already uploaded. They cannot be edited or removed.
        </p>

        <!-- Warning Banner -->
        <div v-if="warningRowCount > 0" class="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800 flex items-start gap-2">
            <span class="shrink-0 mt-0.5">&#9888;</span>
            <span>{{ warningRowCount }} {{ warningRowCount === 1 ? 'row has' : 'rows have' }} warnings on this page. Review them before importing.</span>
        </div>

        <!-- Data Table -->
        <div class="border rounded overflow-hidden">
            <div class="overflow-x-auto max-h-80">
                <table class="w-full text-sm">
                    <thead class="bg-slate-50 sticky top-0 z-10">
                        <tr>
                            <th class="w-10 px-2 py-2 text-left text-slate-500 text-xs">#</th>
                            <th v-for="field in fields" :key="field.key"
                                class="px-3 py-2 text-left text-slate-500 whitespace-nowrap text-xs">
                                {{ field.label }}
                                <span v-if="isRequired(field)" class="text-red-500">*</span>
                            </th>
                            <th v-for="header in customFields" :key="header"
                                class="px-3 py-2 text-left text-blue-500 whitespace-nowrap text-xs">
                                {{ header }}
                                <span class="text-blue-400 text-[10px] ml-1">(custom)</span>
                            </th>
                            <th class="w-10 px-2 py-2"></th>
                        </tr>
                    </thead>
                    <tbody class="divide-y">
                        <tr v-for="record in paginatedRecords" :key="record.id"
                            :class="{ 'bg-red-50': hasErrors(record), 'bg-amber-50': !hasErrors(record) && hasWarnings(record) }">
                            <td class="px-2 py-2 text-xs">
                                <span :class="hasErrors(record) ? 'text-red-600 font-medium' : 'text-slate-400'">
                                    {{ record.id + 1 }}
                                </span>
                                <UTooltip
                                    v-if="hasErrors(record)"
                                    :text="errorSummary(record)"
                                    :delay-duration="0"
                                    :content="{ side: 'right', sideOffset: 6 }"
                                    :ui="{ content: 'z-[1000]' }"
                                    arrow
                                >
                                    <button
                                        type="button"
                                        class="ml-0.5 inline-flex cursor-help align-middle text-red-600"
                                        :aria-label="errorSummary(record)"
                                        :title="errorSummary(record)"
                                    >
                                        <UIcon name="i-heroicons-exclamation-circle" class="h-3.5 w-3.5" />
                                    </button>
                                </UTooltip>
                            </td>
                            <td v-for="field in fields" :key="field.key" class="px-1 py-1">
                                <UTooltip :text="record.errors[field.key] || record.warnings[field.key]" :disabled="!record.errors[field.key] && !record.warnings[field.key]">
                                    <UInput :model-value="record.values[field.key] ?? ''" :disabled="disabled || completedRowIds?.has(record.id)"
                                        @update:model-value="updateCell(record.id, field.key, $event)"
                                        @blur="revalidateRow(record.id)" size="sm"
                                        :ui="{ base: record.errors[field.key] ? 'ring-1 ring-red-500' : record.warnings[field.key] ? 'ring-1 ring-amber-500' : '' }"
                                        class="w-full min-w-24" />
                                </UTooltip>
                                <UTooltip v-if="field.key === 'email' && hasPersonalEmailWarning(record)"
                                    text="This campaign does not send to personal email addresses to help protect deliverability. You can still import this row, but this address will not be used for outreach."
                                    :ui="{ content: 'max-w-xs h-auto', text: 'whitespace-normal' }">
                                    <button type="button" class="mt-1 cursor-help">
                                        <UBadge color="warning" variant="subtle" size="sm">Personal email · won't send</UBadge>
                                    </button>
                                </UTooltip>
                            </td>
                            <td v-for="header in customFields" :key="header" class="px-1 py-1">
                                <UInput :model-value="record.values[header] ?? ''" :disabled="disabled || completedRowIds?.has(record.id)"
                                    @update:model-value="updateCell(record.id, header, $event)"
                                    @blur="revalidateRow(record.id)" size="sm" class="w-full min-w-24" />
                            </td>
                            <td class="px-2 py-2">
                                <UButton icon="i-ion-trash" size="sm" color="error" variant="ghost"
                                    :disabled="disabled || completedRowIds?.has(record.id)" @click="emit('deleteRow', record.id)" />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="flex justify-end mt-3">
            <UPagination v-model:page="currentPage" :disabled="disabled" :total="totalRows" :items-per-page="pageSize" size="sm" />
        </div>

        <!-- Error Summary -->
        <div v-if="errorSummaries.length > 0" class="mt-4 overflow-hidden rounded-lg border border-red-200 bg-red-50/50">
            <div class="border-b border-red-200 px-4 py-3">
                <div class="flex items-center gap-2 text-sm font-medium text-red-900">
                    <UIcon name="i-heroicons-exclamation-triangle" class="h-4 w-4 shrink-0" />
                    Upload errors
                </div>
                <p class="mt-1 text-xs text-red-700">
                    Remove the affected rows in one click, or edit them in the table above.
                </p>
            </div>
            <div class="divide-y divide-red-200">
                <div v-for="summary in errorSummaries" :key="summary.message" class="flex items-center gap-3 px-4 py-3">
                    <div class="min-w-0 flex-1">
                        <p class="text-sm font-medium text-slate-800">{{ summary.message }}</p>
                        <p class="mt-0.5 text-xs text-slate-500">
                            {{ summary.count }} {{ summary.count === 1 ? 'row' : 'rows' }}
                        </p>
                    </div>
                    <UButton
                        color="error"
                        variant="outline"
                        size="sm"
                        icon="i-heroicons-trash"
                        :disabled="disabled"
                        :label="`Remove ${summary.count} ${summary.count === 1 ? 'row' : 'rows'}`"
                        @click="emit('deleteError', summary.message)"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { isPersonalEmail } from "@coldreach/domains/types";

const props = defineProps<{
    records: ImportRecord[];
    totalRows: number;
    invalidRows: number;
    errorSummaries: { message: string; count: number }[];
    fields: FieldDef[];
    customFields?: string[];
    disabled?: boolean;
    completedRowIds?: Set<number>;
    sequenceProvider?: string | null;
}>();

const emit = defineEmits<{
    'update:records': [ImportRecord[]];
    'deleteRow': [number];
    'deleteError': [string];
    'revalidate': [number];
}>();

const currentPage = defineModel<number>('page', { required: true });
const pageSize = 25;

const paginatedRecords = computed(() => props.records);
const totalPages = computed(() => Math.ceil(props.totalRows / pageSize));
const validCount = computed(() => props.totalRows - props.invalidRows);
const errorRowCount = computed(() => props.invalidRows);

const warningRowCount = computed(() =>
    props.records.filter(r => Object.keys(r.errors).length === 0 && hasWarnings(r)).length
);

function isRequired(field: FieldDef): boolean {
    return field.constraints?.some(c => c.type === 'required') ?? false;
}

function hasErrors(record: ImportRecord): boolean {
    return Object.keys(record.errors).length > 0;
}

function errorSummary(record: ImportRecord): string {
    return Object.values(record.errors).join(' · ');
}

function hasPersonalEmailWarning(record: ImportRecord): boolean {
    // Informational only: personal addresses remain importable, and provider eligibility stays server-owned.
    return (props.sequenceProvider === 'smartlead' || props.sequenceProvider === 'emailbison')
        && isPersonalEmail((record.values.email ?? '').trim());
}

function hasWarnings(record: ImportRecord): boolean {
    return Object.keys(record.warnings).length > 0 || hasPersonalEmailWarning(record);
}

function updateCell(recordId: number, fieldKey: string, value: string) {
    if (props.disabled || props.completedRowIds?.has(recordId)) return;
    const updatedRecords = props.records.map(r => {
        if (r.id === recordId) {
            return {
                ...r,
                values: { ...r.values, [fieldKey]: value }
            };
        }
        return r;
    });
    emit('update:records', updatedRecords);
}

function revalidateRow(recordId: number) {
    emit('revalidate', recordId);
}

// Reset to first page when records change significantly
watch(() => props.totalRows, () => {
    if (currentPage.value > totalPages.value) {
        currentPage.value = Math.max(1, totalPages.value);
    }
});
</script>
